import React from 'react'
import PropTypes from 'prop-types'
import {FormattedNumber} from 'react-intl'
import {Button} from 'hec/components/cta'
import {Eyebrow} from '@/components/ui/eyebrow'
import {buttonFunctions} from '../cta'
import {useDetermineRouteTypeAndUrl} from 'hec/hooks/useDetermineRouteTypeAndUrl'
import {useCustomerId} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {isPreviewing, isEditing} from '@builder.io/sdk-react'
import {mockCustomerData} from '../../../data/'

const MinicartFooter = ({
    productTotal,
    orderTotal,
    currency,
    cartPageRef,
    checkoutPageRef,
    splitCTAs,
    approachingDiscounts,
    conditionThreshold
}) => {
    const firstApproachingDiscount = approachingDiscounts?.length ? approachingDiscounts[0] : null
    const {merchandise_total} = firstApproachingDiscount || {}
    const customerID = useCustomerId()
    const isPreview = isPreviewing()
    const isEdit = isEditing()

    const checkoutCTA = splitCTAs ? 'View Cart' : 'View Cart & Checkout'

    const {
        data: customerData,
        isError: isCustomerError,
        error: customerError
    } = useCurrentCustomer()
    const customer = isEdit ? mockCustomerData : customerData
    const isLoggedIn = !customer.isGuest && customer.isRegistered
    const clickFunctionType = isLoggedIn ? buttonFunctions.linkToPage : buttonFunctions.nothing
    const {url, routeType} = useDetermineRouteTypeAndUrl({
        clickFunction: buttonFunctions.linkToPage,
        isInternalLink: true,
        pageReference: cartPageRef
    })

    const checkoutUrl = useDetermineRouteTypeAndUrl({
        clickFunction: buttonFunctions.linkToPage,
        isInternalLink: true,
        pageReference: checkoutPageRef
    })

    // Use hash navigation for account drawer (new approach)
    // Keeping the event-based approach as fallback for compatibility
    const handleCheckoutClick = () => {
        if (!isLoggedIn) {
            // TODO: Replace with hash navigation
            window.location.hash = 'login'
            const OpenDrawer = new CustomEvent('openDrawer', {
                bubbles: true,
                detail: {checkoutPageRef: checkoutPageRef, enableGuestCheckout: splitCTAs}
            })
            document.dispatchEvent(OpenDrawer)
        }
    }

    return (
        <div className="flex flex-col py-[1.875rem] px-[0.9375rem] border-t bg-gray-01">
            {firstApproachingDiscount || productTotal <= conditionThreshold ? (
                <div className="mb-2 text-sm text-accent">
                    <FormattedNumber
                        value={conditionThreshold - (merchandise_total ?? productTotal)}
                        style="currency"
                        currency={currency}
                        data-testid="minicartFooter-FormattedNumber"
                    />{' '}
                    away from FREE shipping!
                </div>
            ) : productTotal >= conditionThreshold ? (
                <div className="mb-2 text-sm text-accent">You earned FREE shipping!</div>
            ) : (
                ''
            )}
            <div className="flex justify-between mb-4">
                <p className="text-emphasis">Estimated Total:</p>
                <p>
                    <Eyebrow
                        size="large"
                        className="text-emphasis"
                        data-testid="minicartFooter-Eyebrow"
                        type="text"
                    >
                        <FormattedNumber
                            style="currency"
                            currency={currency}
                            value={orderTotal}
                            data-testid="minicartFooter-FormattedNumber"
                        />
                    </Eyebrow>
                </p>
            </div>
            <div className="flex flex-row gap-2">
                <Button
                    ctaType="button"
                    clickFunction={buttonFunctions.linkToPage}
                    url={url}
                    routeType={routeType}
                    variant="primary"
                    className="w-full"
                    data-testid="minicartFooter-Button"
                >
                    {checkoutCTA}
                </Button>
                {splitCTAs && (
                    <Button
                        ctaType="button"
                        clickFunction={clickFunctionType}
                        onClick={handleCheckoutClick}
                        url={checkoutUrl.url}
                        routeType={checkoutUrl.routeType}
                        variant="secondary"
                        className="w-full"
                        data-testid="minicartFooter-CheckoutButton"
                    >
                        Checkout
                    </Button>
                )}
            </div>
        </div>
    )
}

MinicartFooter.propTypes = {
    productTotal: PropTypes.number.isRequired,
    orderTotal: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    cartPageRef: PropTypes.shape({
        value: PropTypes.shape({
            data: PropTypes.shape({
                isSfraPage: PropTypes.bool,
                url: PropTypes.string
            })
        })
    }),
    checkoutPageRef: PropTypes.shape({
        value: PropTypes.shape({
            data: PropTypes.shape({
                isSfraPage: PropTypes.bool,
                url: PropTypes.string
            })
        })
    }),
    approachingDiscounts: PropTypes.array,
    conditionThreshold: PropTypes.number,
    splitCTAs: PropTypes.bool
}

export default MinicartFooter
